import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Button,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  AccountBalance as AccountBalanceIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  Info as InfoIcon,
  History as HistoryIcon,
  Description as InvoiceIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../../../utils/config';
import { NotesSection } from '../../../../components/accounting/NotesSection';

interface ConfidenceBreakdown {
  invoiceBillMapping: number;
  customerVendorMatch: number;
  exactAmountMatch: number;
  referenceMatch: number;
  dueDateProximity: number;
}

interface MatchedBill {
  quickbooksId: string;
  docNumber: string;
  vendorName: string;
  vendorId: string;
  totalAmount: number;
  balance: number;
  dueDate: string;
}

interface AuditEntry {
  id: number;
  queueReference: string;
  action: string;
  previousStatus: string;
  newStatus: string;
  userId: string;
  userEmail: string;
  userDisplayName: string;
  details: any;
  ipAddress: string;
  createdAt: string;
}

interface CustomerInvoice {
  quickbooksId: string;
  docNumber: string;
  customerName: string;
  totalAmount: number;
  balance: number;
  txnDate: string;
  dueDate: string;
  mappingReason: string;
}

interface SubsidiaryInvoice {
  id: number;
  invoiceNumber: string;
  vendorName: string;
  totalAmount: number;
  currency: string;
  invoiceDate: string;
  paidDate: string | null;
  quickbooksBillId: string;
}

interface QueueItemDetails {
  id: number;
  queueReference: string;
  sourceCompany: string;
  paymentType: string;
  sourceQuickbooksId: string;
  sourcePaymentRefNum: string;
  sourceTxnDate: string;
  sourceAmount: number;
  sourceCustomerRefValue: string;
  sourceCustomerName: string;
  sourceDepositAccountRef: string;
  sourcePaymentMethod: string;
  matchConfidence: string;
  confidenceScore: number;
  confidenceBreakdown: ConfidenceBreakdown;
  matchedBillIds: string[];
  matchedBillAmount: number;
  targetCompany: string;
  status: string;
  requiresDualApproval: boolean;
  firstApprovalBy: string;
  firstApprovalAt: string;
  approvedBy: string;
  approvedAt: string;
  rejectedBy: string;
  rejectedAt: string;
  rejectionReason: string;
  executedAt: string;
  executionPaymentId: string;
  mirrorTransactionId: string;
  executionError: string;
  executionRetryCount: number;
  priority: string;
  detectionSource: string;
  createdAt: string;
  updatedAt: string;
  matchedBillDetails: MatchedBill[];
  customerInvoices: CustomerInvoice[];
  subsidiaryInvoices: SubsidiaryInvoice[];
  auditHistory: AuditEntry[];
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateString: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getStatusChip = (status: string) => {
  const statusConfig: Record<string, { color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'; icon: React.ReactElement; label: string }> = {
    pending: { color: 'warning', icon: <PendingIcon fontSize="small" />, label: 'Pending' },
    submitted: { color: 'info', icon: <PendingIcon fontSize="small" />, label: 'Submitted' },
    executed: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Paid' },
  };

  const config = statusConfig[status] || { color: 'default' as const, icon: <PendingIcon fontSize="small" />, label: status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) };

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      sx={{ fontWeight: 'bold' }}
    />
  );
};

const getConfidenceChipColor = (confidence: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (confidence) {
    case 'high':
      return 'success';
    case 'medium':
      return 'warning';
    case 'low':
    case 'manual_required':
      return 'error';
    default:
      return 'default';
  }
};

const getConfidenceProgressColor = (confidence: string): 'success' | 'warning' | 'error' | 'inherit' => {
  switch (confidence) {
    case 'high':
      return 'success';
    case 'medium':
      return 'warning';
    case 'low':
    case 'manual_required':
      return 'error';
    default:
      return 'inherit';
  }
};

const getActionLabel = (action: string) => {
  const actionLabels: Record<string, string> = {
    created: 'Queue Entry Created',
    approved: 'Approved',
    first_approved: 'First Approval Granted',
    rejected: 'Rejected',
    manual_match: 'Manual Match Updated',
    execution_started: 'Execution Started',
    executed: 'Payment Executed',
    execution_failed: 'Execution Failed',
    retry_attempted: 'Retry Attempted',
  };
  return actionLabels[action] || action;
};

const getActionColor = (action: string): 'success' | 'error' | 'info' | 'warning' | 'grey' => {
  switch (action) {
    case 'approved':
    case 'executed':
      return 'success';
    case 'rejected':
    case 'execution_failed':
      return 'error';
    case 'created':
    case 'manual_match':
      return 'info';
    case 'first_approved':
    case 'execution_started':
    case 'retry_attempted':
      return 'warning';
    default:
      return 'grey';
  }
};

export const ReconciliationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();

  const [item, setItem] = useState<QueueItemDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchItem = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authenticatedFetch(`${API_URL}/api/accounting/reconciliation/queue/${id}`);
      if (!response) {
        throw new Error('No response received');
      }
      const data = await response.json();

      if (data.success) {
        setItem(data.data);
      } else {
        setError(data.error || 'Failed to load item');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load item');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, id]);

  useEffect(() => {
    fetchItem();
  }, [fetchItem]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !item) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={() => navigate('/admin/hub/accounting/reconciliation')}>
          Back to Queue
        </Button>
      </Container>
    );
  }

  if (!item) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info">Queue item not found.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => navigate('/admin/hub')}
        >
          Admin Hub
        </Link>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => navigate('/admin/hub/accounting/reconciliation')}
        >
          Reconciliation
        </Link>
        <Typography color="text.primary">{item.queueReference}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/admin/hub/accounting/reconciliation')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <AccountBalanceIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1">
              {item.queueReference}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              {getStatusChip(item.status)}
              <Chip
                label={item.sourceCompany === 'horwath' ? 'Horwath' : 'Barrett'}
                size="small"
                variant="outlined"
              />
              <Chip
                label={item.paymentType.replace(/_/g, ' ')}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={fetchItem} title="Refresh">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column - Payment Details */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 3 }}>
            <CardHeader
              avatar={<ReceiptIcon color="primary" />}
              title="Payment Details"
              subheader={`Detected via ${item.detectionSource}`}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Amount</Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    {formatCurrency(item.sourceAmount)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Payment Date</Typography>
                  <Typography variant="body1">{formatDate(item.sourceTxnDate)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Customer</Typography>
                  <Typography variant="body1">{item.sourceCustomerName || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Reference #</Typography>
                  <Typography variant="body1">{item.sourcePaymentRefNum || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Payment Method</Typography>
                  <Typography variant="body1">{item.sourcePaymentMethod || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">QuickBooks ID</Typography>
                  <Typography variant="body1" fontFamily="monospace">{item.sourceQuickbooksId}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Confidence Score Card */}
          <Card sx={{ mb: 3 }}>
            <CardHeader
              avatar={<InfoIcon color="primary" />}
              title="Match Confidence"
              action={
                <Chip
                  label={`${item.confidenceScore.toFixed(0)}%`}
                  color={getConfidenceChipColor(item.matchConfidence)}
                  size="small"
                />
              }
            />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={item.confidenceScore}
                  color={getConfidenceProgressColor(item.matchConfidence)}
                  sx={{ height: 10, borderRadius: 1 }}
                />
              </Box>
              <Typography variant="subtitle2" gutterBottom>Score Breakdown:</Typography>
              {item.confidenceBreakdown && (
                <Grid container spacing={1}>
                  <Grid item xs={8}>
                    <Typography variant="body2" color="text.secondary">Invoice-Bill Mapping</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" align="right">{item.confidenceBreakdown.invoiceBillMapping || 0}/50</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2" color="text.secondary">Customer/Vendor Match</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" align="right">{item.confidenceBreakdown.customerVendorMatch || 0}/20</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2" color="text.secondary">Exact Amount Match</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" align="right">{item.confidenceBreakdown.exactAmountMatch || 0}/20</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2" color="text.secondary">Reference Match</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" align="right">{item.confidenceBreakdown.referenceMatch || 0}/15</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2" color="text.secondary">Due Date Proximity</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" align="right">{item.confidenceBreakdown.dueDateProximity || 0}/10</Typography>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Matched Bills and Audit */}
        <Grid item xs={12} md={6}>
          {/* Matched Bills */}
          <Card sx={{ mb: 3 }}>
            <CardHeader
              avatar={<MoneyIcon color="primary" />}
              title="Matched Bills"
              subheader={item.targetCompany ? `Target: ${item.targetCompany === 'horwath' ? 'Horwath' : 'Barrett'}` : 'No target'}
            />
            <CardContent>
              {item.matchedBillDetails && item.matchedBillDetails.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Bill #</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="right">Balance</TableCell>
                        <TableCell>Due Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {item.matchedBillDetails.map((bill) => (
                        <TableRow key={bill.quickbooksId}>
                          <TableCell>
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => navigate(`/admin/hub/accounting/bills/${bill.quickbooksId}`)}
                            >
                              {bill.docNumber || bill.quickbooksId}
                            </Link>
                          </TableCell>
                          <TableCell>{bill.vendorName}</TableCell>
                          <TableCell align="right">{formatCurrency(bill.totalAmount)}</TableCell>
                          <TableCell align="right">{formatCurrency(bill.balance)}</TableCell>
                          <TableCell>{formatDate(bill.dueDate)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="warning">
                  No bills matched. Use Manual Match to select bills.
                </Alert>
              )}
              {item.matchedBillAmount > 0 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Typography variant="subtitle1">
                    Total: <strong>{formatCurrency(item.matchedBillAmount)}</strong>
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Customer Invoice (from source company QuickBooks) */}
          {item.customerInvoices && item.customerInvoices.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardHeader
                avatar={<InvoiceIcon color="primary" />}
                title="Customer Invoice"
                subheader={`Invoice from ${item.sourceCompany === 'horwath' ? 'Horwath' : 'Barrett'} QuickBooks`}
              />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice #</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Balance</TableCell>
                        <TableCell>Invoice Date</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {item.customerInvoices.map((invoice) => (
                        <TableRow key={invoice.quickbooksId}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {invoice.docNumber || invoice.quickbooksId}
                            </Typography>
                          </TableCell>
                          <TableCell>{invoice.customerName}</TableCell>
                          <TableCell align="right">{formatCurrency(invoice.totalAmount)}</TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              color={invoice.balance === 0 ? 'success.main' : 'warning.main'}
                              fontWeight="medium"
                            >
                              {formatCurrency(invoice.balance)}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatDate(invoice.txnDate)}</TableCell>
                          <TableCell>
                            <Chip
                              label={invoice.balance === 0 ? 'Paid' : 'Outstanding'}
                              color={invoice.balance === 0 ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Subsidiary Invoice (from vendor/subsidiary) */}
          {item.subsidiaryInvoices && item.subsidiaryInvoices.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardHeader
                avatar={<BusinessIcon color="primary" />}
                title="Subsidiary Invoice"
                subheader="Invoice from subsidiary vendor"
              />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice #</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Invoice Date</TableCell>
                        <TableCell>Paid Date</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {item.subsidiaryInvoices.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {invoice.invoiceNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>{invoice.vendorName}</TableCell>
                          <TableCell align="right">{formatCurrency(invoice.totalAmount)}</TableCell>
                          <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                          <TableCell>
                            {invoice.paidDate ? formatDate(invoice.paidDate) : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={invoice.paidDate ? 'Paid' : 'Unpaid'}
                              color={invoice.paidDate ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Payment Details (if paid) */}
          {item.status === 'executed' && (
            <Card sx={{ mb: 3 }}>
              <CardHeader
                avatar={<CheckCircleIcon color="success" />}
                title="Payment Details"
              />
              <CardContent>
                {item.executedAt && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">Paid At</Typography>
                    <Typography variant="body1">{formatDateTime(item.executedAt)}</Typography>
                  </Box>
                )}
                {item.executionPaymentId && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">Payment ID</Typography>
                    <Typography variant="body1" fontFamily="monospace">{item.executionPaymentId}</Typography>
                  </Box>
                )}
                {item.mirrorTransactionId && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">Mirror Transaction ID</Typography>
                    <Typography variant="body1" fontFamily="monospace">{item.mirrorTransactionId}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Full Width - Audit History */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              avatar={<HistoryIcon color="primary" />}
              title="Audit History"
            />
            <CardContent>
              {item.auditHistory && item.auditHistory.length > 0 ? (
                <Timeline position="alternate">
                  {item.auditHistory.map((entry, index) => (
                    <TimelineItem key={entry.id}>
                      <TimelineOppositeContent color="text.secondary">
                        {formatDateTime(entry.createdAt)}
                      </TimelineOppositeContent>
                      <TimelineSeparator>
                        <TimelineDot color={getActionColor(entry.action)} />
                        {index < item.auditHistory.length - 1 && <TimelineConnector />}
                      </TimelineSeparator>
                      <TimelineContent>
                        <Typography variant="subtitle2" component="span">
                          {getActionLabel(entry.action)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          by {entry.userDisplayName || entry.userEmail}
                        </Typography>
                        {entry.details && entry.details.notes && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            Notes: {entry.details.notes}
                          </Typography>
                        )}
                        {entry.details && entry.details.reason && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            Reason: {entry.details.reason}
                          </Typography>
                        )}
                      </TimelineContent>
                    </TimelineItem>
                  ))}
                </Timeline>
              ) : (
                <Typography color="text.secondary">No audit history available.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Notes Section - Shows notes from all linked invoices and bills */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <NotesSection
                entityType="bill"
                entityId={item.matchedBillIds?.[0] || ''}
                showTypeColumn={true}
                invoiceIds={item.customerInvoices?.map((inv: CustomerInvoice) => inv.quickbooksId) || []}
                billIds={item.matchedBillIds || []}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Container>
  );
};

export default ReconciliationDetailPage;
